<template>
  <div class="overflow-visible relative p-4 grid" :class="`layout-${layout}`">
    <ScoreCircle
      class="absolute -top-9 right-0 md:-right-5 z-10"
      :class="circleClass"
      :score="scoreSummary.score?.value"
    />
    <div v-if="isWinner" class="absolute left-0 right-0 flex justify-center z-10">
      <div class="winner-label-container bg-white p-2 pt-0">
        <div
          class="winner-label font-bold text-white px-2 py-1"
          :class="`bg-score-${getScoreLabel(scoreSummary.score?.value)}`"
        >
          {{ $t('scoring.winner') }}
        </div>
      </div>
    </div>

    <div class="sidebar-header pt-16 pb-10">
      <a
        :href="scoreSummary.cta.url ?? undefined"
        :target="scoreSummary.cta.target ?? undefined"
        :rel="scoreSummary.cta.url ? relAttribute : undefined"
        :aria-label="scoreSummary.cta.label ?? undefined"
        class="block mx-auto w-3/4 h-20"
      >
        <template v-if="type === 'migration'">
          <span class="subtitle2 text-center block mb-3">{{ scoreSummary.displayName }}</span>
          <img :src="scoreSummary.logo ?? undefined" alt="" class="max-h-14 m-auto rounded-4" />
        </template>
        <template v-else>
          <BackgroundImage
            v-if="scoreSummary.logo"
            class="h-full"
            size="contain"
            :src="scoreSummary.logo"
            :alt="scoreSummary.cta.label ?? ''"
            :lazy="scoreSummary.cta.asLink"
          />
        </template>
      </a>
    </div>
    <div class="categories pt-5">
      <ul>
        <li
          v-for="(category, index) in categories"
          :key="`category-${index}`"
          :class="{ active: activeCategory === category.name }"
          class="section px-3 py-1 relative duration-300 ease-out hover:bg-gray-50 lg:px-7 lg:-mx-4"
        >
          <a
            :href="scoreSummary.url ? `${scoreSummary.url}#${category.name}` : undefined"
            class="pt-0.5 block"
          >
            <span class="text-16 tracking-wide font-semibold">
              {{ category.label }}
            </span>
            <span class="mb-0.5 block">
              <ScoreBar :score="category.value" />
            </span>
          </a>
        </li>
      </ul>
      <a
        v-if="scoreSummary.cta.url"
        :href="scoreSummary.cta.url ?? undefined"
        :target="scoreSummary.cta.target ?? undefined"
        :class="scoreSummary.cta.asLink ? 'block py-3 link mt-1' : 'btn btn-amber mt-5'"
        class="w-full text-center"
        :rel="relAttribute"
      >
        {{ scoreSummary.cta.label }}
      </a>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { ScoreSummaryEntity, ScoreSummaryType } from './types'

const props = withDefaults(
  defineProps<{
    scoreSummary: ScoreSummaryEntity
    type: ScoreSummaryType
    isWinner?: boolean
    activeCategory?: string | null
    circleClass?: string
    layout?: 'vertical' | 'horizontal' | 'auto'
  }>(),
  {
    layout: 'auto',
  },
)

const {
  public: { redirectUrl },
} = useRuntimeConfig()

const categories = computed(() =>
  props.scoreSummary.score
    ? props.scoreSummary.score.categories
    : [
        { name: '', label: '\u200B', value: undefined },
        { name: '', label: '\u200B', value: undefined },
        { name: '', label: '\u200B', value: undefined },
        { name: '', label: '\u200B', value: undefined },
      ],
)

const relAttribute = computed(() =>
  props.scoreSummary.cta.url?.startsWith(redirectUrl) ? 'nofollow noopener' : '',
)
</script>

<style scoped>
.section {
  transition-property: padding-top, padding-bottom, background-color;
}

.winner-label {
  @apply rounded-b-12;
  box-shadow: inset 0px 50px 0px rgb(0 0 0 / 20%);
}

.winner-label-container {
  border-radius: 0px 0px 18px 18px;
}

@screen sm-lg {
  .layout-auto {
    @apply layout-horizontal;
  }
}

@screen sm {
  .layout-horizontal {
    @apply p-7 pr-9 grid-cols-2;
  }

  .layout-horizontal .sidebar-header {
    @apply flex flex-col justify-center pb-8 rounded-tr-none rounded-bl-12;
    clip-path: polygon(0 0, calc(100% - 20px) 0, 100% 50%, calc(100% - 20px) 100%, 0 100%);
  }

  .layout-horizontal .categories {
    @apply pl-9 pt-9;
  }
}

@screen lg {
  .section::before {
    content: '';
    transition-property: border-right;

    @apply absolute top-0 right-full h-full border-0 border-transparent duration-150 ease-in;
  }

  .active::before {
    border-top: 40px solid transparent;
    border-bottom: 40px solid transparent;
    border-right: 15px solid theme('colors.blue.50');
  }

  .active {
    @apply lg:bg-blue-50 lg:py-4 lg:pl-6 lg:pr-5;
  }
}
</style>
